"use client";

import { c as _c } from "react/compiler-runtime";
import { ApplicationProvider } from "@modules/application/context/ApplicationContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createInstance, type Resource } from "i18next";
import mitt from "next/dist/shared/lib/mitt";
import { RouterContext } from "next/dist/shared/lib/router-context.shared-runtime";
import { formatUrl } from "next/dist/shared/lib/router/utils/format-url";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import type { NextRouter } from "next/router";
import { type PropsWithChildren, useMemo, useState } from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";
import type { UrlObject } from "url";
const toUrl = (url: string | UrlObject) => typeof url === "string" ? url : formatUrl(url);
const usePagesRouter = () => {
  const $ = _c(21);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [events] = useState(mitt);
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.toString();
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const search = t0;
  let t1;
  if ($[2] !== router) {
    t1 = method => async url => {
      router[method](toUrl(url));
      return true;
    };
    $[2] = router;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const navigate = t1;
  let t2;
  if ($[4] !== searchParams) {
    t2 = Object.fromEntries(searchParams);
    $[4] = searchParams;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = search ? `${pathname}?${search}` : pathname;
  let t4;
  let t5;
  if ($[6] !== navigate) {
    t4 = navigate("push");
    t5 = navigate("replace");
    $[6] = navigate;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  let t7;
  if ($[9] !== router) {
    t6 = () => router.back();
    t7 = () => router.forward();
    $[9] = router;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] !== events || $[13] !== pathname || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = {
      route: pathname,
      pathname,
      query: t2,
      asPath: t3,
      basePath: "",
      isLocaleDomain: false,
      isReady: true,
      isPreview: false,
      isFallback: false,
      push: t4,
      replace: t5,
      reload: _temp,
      back: t6,
      forward: t7,
      prefetch: _temp2,
      beforePopState: _temp3,
      events
    };
    $[12] = events;
    $[13] = pathname;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
const createI18n = (language: string, resources: Resource) => {
  const i18n = createInstance();

  // The same defaults next-i18next sets, so a key reads the same in both routers.
  void i18n.use(initReactI18next).init({
    lng: language,
    fallbackLng: "de",
    resources,
    defaultNS: "common",
    ns: Object.keys(resources[language] ?? {}),
    interpolation: {
      escapeValue: false
    },
    initImmediate: false
  });
  return i18n;
};

// The context `_app.tsx` gives Pages Router pages. Needs a Suspense boundary: it reads the
// search params.
const LegacyEmbedBridge = t0 => {
  const $ = _c(14);
  const {
    language,
    resources,
    children
  } = t0;
  const router = usePagesRouter();
  let t1;
  if ($[0] !== language || $[1] !== resources) {
    t1 = () => createI18n(language, resources);
    $[0] = language;
    $[1] = resources;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [i18n] = useState(t1);
  const [queryClient] = useState(_temp4);
  let t2;
  if ($[3] !== children) {
    t2 = <ApplicationProvider>{children}</ApplicationProvider>;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== queryClient || $[6] !== t2) {
    t3 = <QueryClientProvider client={queryClient}>{t2}</QueryClientProvider>;
    $[5] = queryClient;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== i18n || $[9] !== t3) {
    t4 = <I18nextProvider i18n={i18n}>{t3}</I18nextProvider>;
    $[8] = i18n;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== router || $[12] !== t4) {
    t5 = <RouterContext.Provider value={router}>{t4}</RouterContext.Provider>;
    $[11] = router;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export default LegacyEmbedBridge;
function _temp() {
  return window.location.reload();
}
async function _temp2() {}
function _temp3() {}
function _temp4() {
  return new QueryClient();
}